import { connect } from 'react-redux';
import * as ComponentFunctions from './BilledInConcept.List.Functions';
import { useEffect, useMemo, useState } from 'react';
import { createTableColumn, TableColumnDefinition, DataGrid, DataGridHeader, DataGridRow, DataGridHeaderCell, DataGridBody, DataGridCell, Spinner, Badge } from '@fluentui/react-components';
import { ProtocolConsumption } from '../../../models/Entities/Protocols/ProtocolConsumption';
import ConsumptionDetailsCompoment from '../ConsumptionDetails/ConsumptionDetails.Component';
import { useTranslation } from 'react-i18next';
import { getConceptValueColumnAmounts, getConsumptionAmounts } from '../ConsumptionList/ConsumptionList.Functions';

// styles
import './BilledInConcept.List.Styles.css';

const getNetAmountForGrid = (item: ProtocolConsumption): number => {
  if (item.totalAmountWithWithholding !== undefined && item.totalAmountWithWithholding !== null) {
    return item.totalAmountWithWithholding;
  }

  const mainNet = (item.amount ?? 0) - (item.withHolingAmount ?? 0);
  const conceptValuesNet = (item.conceptValues ?? []).reduce((acc, cv) => {
    const conceptGross = cv.value ?? 0;
    const conceptWithholding = cv.applyWithholding ? (cv.withholdingAmount ?? 0) : 0;
    return acc + (conceptGross - conceptWithholding);
  }, 0);

  return mainNet + conceptValuesNet;
};

// ─── Formato ─────────────────────────────────────────────────────────────────

const currencyCodeOf = (currency: string | undefined): string => {
  const normalized = (currency ?? '').trim().toUpperCase();
  return /^[A-Z]{3}$/.test(normalized) ? normalized : 'ARS';
};

/** Importe sin simbolo: la moneda se declara una sola vez en la franja superior. */
const fmtAmount = (value: number | undefined): string =>
  (value ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtCurrency = (value: number | undefined, currency: string | undefined): string => {
  // Intl lanza si el codigo no es ISO de 3 letras.
  const safeCurrency = currencyCodeOf(currency);
  try {
    return (value ?? 0).toLocaleString('es-AR', { style: 'currency', currency: safeCurrency });
  } catch {
    return (value ?? 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
  }
};

const fmtPercent = (value: number | undefined): string =>
  `${(value ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 3 })}%`;

// ─── Celdas ──────────────────────────────────────────────────────────────────

const NumCell = (props: { value?: string; note?: string; tone?: 'strong' | 'negative' }) => {
  if (!props.value) {
    return <span className="bgrid-num-empty">—</span>;
  }

  return (
    <div className={`bgrid-num${props.tone ? ` bgrid-num--${props.tone}` : ''}`}>
      <span className="bgrid-num-value">{props.value}</span>
      {props.note && <span className="bgrid-num-note">{props.note}</span>}
    </div>
  );
};

// ─── Totales ─────────────────────────────────────────────────────────────────

/** Suma por moneda: sumar importes de monedas distintas daria un numero falso. */
type CurrencyTotals = { count: number; byCurrency: Map<string, number> };

const emptyTotals = (): CurrencyTotals => ({ count: 0, byCurrency: new Map<string, number>() });

const addToTotals = (totals: CurrencyTotals, item: ProtocolConsumption): void => {
  const code = currencyCodeOf(item.currencyCode);
  totals.count += 1;
  totals.byCurrency.set(code, (totals.byCurrency.get(code) ?? 0) + getNetAmountForGrid(item));
};

// ─────────────────────────────────────────────────────────────────────────────

const BilledInConceptListComponent = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (props.ProtocolId && (props.protocol === null || props.protocol === undefined)) {
      // Ensure protocol is loaded
      props.GetMedicalProtocolById(props.ProtocolId);
    }
    // Los consumos facturados dentro del concepto se piden al servidor a
    // partir del consumo actual; el backend resuelve el resto del criterio.
    if (props.ProtocolId && props.CosnumptionId) {
      props.GetConsumptionsBilledInConcept(props.ProtocolId, props.CosnumptionId);
    }
  }, [props.ProtocolId, props.CosnumptionId]);

  const [selectedConsumption, setSelectedConsumption] = useState<ProtocolConsumption | null>(null);
  const [isInformationPanelOpen, setIsInformationPanelOpen] = useState(false);

  const onInformationPanelDismiss = () => {
    setSelectedConsumption(null);
    setIsInformationPanelOpen(false);
  };

  const openConsumption = (item: ProtocolConsumption) => {
    setSelectedConsumption(item);
    setIsInformationPanelOpen(true);
  };

  const items = props.Consumptions ?? [];

  // Si todas las filas comparten moneda se declara una vez arriba y las celdas
  // quedan sin simbolo, que es lo que permite alinear los decimales en columna.
  // Con monedas mezcladas cada importe conserva su simbolo.
  const sharedCurrency = useMemo(() => {
    const codes = new Set(items.map((item) => currencyCodeOf(item.currencyCode)));
    return codes.size === 1 ? Array.from(codes)[0] : undefined;
  }, [items]);

  const money = (value: number | undefined, item: ProtocolConsumption): string =>
    sharedCurrency ? fmtAmount(value) : fmtCurrency(value, item.currencyCode);

  // Los consumos con isBilledConceptCost forman parte del costo de la visita;
  // el resto no, asi que los totales se llevan por separado.
  const totals = useMemo(() => {
    const cost = emptyTotals();
    const nonCost = emptyTotals();

    items.forEach((item) => addToTotals(item.isBilledConceptCost ? cost : nonCost, item));

    return { cost, nonCost };
  }, [items]);

  /** Una linea por moneda; con moneda unica el simbolo lo aporta la franja superior. */
  const fmtTotals = (totals: CurrencyTotals): string => {
    if (totals.byCurrency.size === 0) {
      return sharedCurrency ? fmtAmount(0) : fmtCurrency(0, sharedCurrency);
    }

    return Array.from(totals.byCurrency.entries())
      .map(([code, sum]) => (sharedCurrency ? fmtAmount(sum) : fmtCurrency(sum, code)))
      .join('  +  ');
  };


  // Una columna por concepto liquidado, igual que en la grilla principal de
  // consumos: sin esto los conceptValues no se ven en ningun lado de esta
  // grilla. Se agrupan por nombre porque el mismo concepto puede venir con
  // distinto contractConceptId entre adendas.
  const conceptColumns = useMemo<TableColumnDefinition<ProtocolConsumption>[]>(() => {
    const conceptNames = new Set<string>();
    items.forEach((item) => {
      (item.conceptValues ?? []).forEach((conceptValue) => {
        if (conceptValue?.contractConceptName) {
          conceptNames.add(conceptValue.contractConceptName);
        }
      });
    });

    const totalFor = (item: ProtocolConsumption, conceptName: string) =>
      (item.conceptValues ?? [])
        .filter((conceptValue) => conceptValue.contractConceptName === conceptName)
        .reduce((sum, conceptValue) => sum + (conceptValue.value ?? 0), 0);

    return Array.from(conceptNames)
      .sort((left, right) => left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' }))
      .map((conceptName) =>
        createTableColumn<ProtocolConsumption>({
          columnId: `concept-value-${conceptName}`,
          compare: (a, b) => totalFor(a, conceptName) - totalFor(b, conceptName),
          renderHeaderCell: () => <span title={conceptName}>{conceptName}</span>,
          renderCell: (item: ProtocolConsumption) => {
            const matches = (item.conceptValues ?? []).filter(
              (conceptValue) => conceptValue.contractConceptName === conceptName
            );
            if (matches.length === 0) return <NumCell />;

            // Mismo criterio que la grilla principal: el valor neto de su
            // retencion, y la retencion descontada como nota.
            const column = getConceptValueColumnAmounts(item, conceptName);

            return (
              <NumCell
                value={money(column.net, item)}
                note={column.withholding ? `−${money(column.withholding, item)}` : undefined}
              />
            );
          },
        })
      );
  }, [items, sharedCurrency]);

  const columns: TableColumnDefinition<ProtocolConsumption>[] = [
    createTableColumn<ProtocolConsumption>({
      columnId: 'concept',
      compare: (a, b) => a.serviceName.localeCompare(b.serviceName),
      renderHeaderCell: () => <span>{t('consumptionList.headers.concept')}</span>,
      renderCell: (item: ProtocolConsumption) => <div className="bgrid-concept">{item.serviceName}</div>,
    }),
    createTableColumn<ProtocolConsumption>({
      columnId: 'cost',
      compare: (a, b) => Number(a.isBilledConceptCost) - Number(b.isBilledConceptCost),
      renderHeaderCell: () => <span>{t('billedInConceptList.visitCost')}</span>,
      // El badge lleva texto propio: no alcanza con un color o un tilde suelto
      // para saber cual de los dos grupos suma al costo.
      renderCell: (item: ProtocolConsumption) =>
        item.isBilledConceptCost ? (
          <Badge appearance="tint" color="informative" size="small" title={t('billedInConceptList.isVisitCost')}>
            {t('billedInConceptList.costBadge')}
          </Badge>
        ) : (
          <span className="bgrid-num-empty" title={t('billedInConceptList.isNotVisitCost')}>
            —
          </span>
        ),
    }),
    createTableColumn<ProtocolConsumption>({
      columnId: 'amount',
      compare: (a, b) => getNetAmountForGrid(a) - getNetAmountForGrid(b),
      renderHeaderCell: () => <span>{t('consumptionList.headers.amount')}</span>,
      renderCell: (item: ProtocolConsumption) => (
        <NumCell value={money(getNetAmountForGrid(item), item)} tone="strong" />
      ),
    }),
    ...conceptColumns,
    createTableColumn<ProtocolConsumption>({
      columnId: 'withholding',
      compare: (a, b) => getConsumptionAmounts(a).withholding - getConsumptionAmounts(b).withholding,
      renderHeaderCell: () => <span>{t('consumptionList.headers.withholding')}</span>,
      // Retencion total: la del principal mas la de cada valor de concepto.
      renderCell: (item: ProtocolConsumption) => {
        const amounts = getConsumptionAmounts(item);
        const note = amounts.mainWithholding > 0 && amounts.valuesWithholding > 0
          ? `${money(amounts.mainWithholding, item)} + ${money(amounts.valuesWithholding, item)}`
          : amounts.mainWithholding > 0 ? fmtPercent(item.withHolingPercentage) : undefined;
        return (
          <NumCell
            value={amounts.withholding > 0 ? `−${money(amounts.withholding, item)}` : undefined}
            note={note}
            tone="negative"
          />
        );
      },
    }),
  ];

  const Forms = (
    <>
      {selectedConsumption && (
        <ConsumptionDetailsCompoment
          ProtocolId={selectedConsumption.protocolId}
          ParentProtocolConsumptionId={props.CosnumptionId}
          ProtocolConsumptionId={selectedConsumption.id}
          visitId={undefined}
          isOpen={isInformationPanelOpen}
          onClose={onInformationPanelDismiss}
        />
      )}
    </>
  );

  return (
    <>
      <div className="billedGrid">
        <div className="billedGrid-toolbar">
          <span className="billedGrid-count">
            {items.length} {items.length === 1 ? 'concepto' : 'conceptos'}
          </span>
          {sharedCurrency && <span className="billedGrid-currency">Importes en {sharedCurrency}</span>}
        </div>

        {/* Solo se reemplaza la grilla por el spinner cuando todavia no hay nada
            que mostrar; una recarga en segundo plano no vacia la seccion. */}
        {items.length === 0 && props.isLoading && (
          <div className="billedGrid-state">
            <Spinner size="tiny" label={t('billedInConceptList.loading')} />
          </div>
        )}

        {items.length === 0 && !props.isLoading && props.error && (
          <div className="billedGrid-state billedGrid-state--error">
            {t('billedInConceptList.error')}
          </div>
        )}

        {items.length === 0 && !props.isLoading && !props.error && (
          <div className="billedGrid-state">{t('billedInConceptList.empty')}</div>
        )}

        {items.length > 0 && (
          <>
            <div className="billedGrid-scroll">
              <DataGrid
                items={items}
                columns={columns}
                className="billedDataGrid"
                size="small"
                sortable
              >
                <DataGridHeader>
                  <DataGridRow>
                    {(column) => (
                      <DataGridHeaderCell className={`bgrid-cell bgrid-cell--${column.columnId}`}>
                        {column.renderHeaderCell()}
                      </DataGridHeaderCell>
                    )}
                  </DataGridRow>
                </DataGridHeader>
                <DataGridBody<ProtocolConsumption>>
                  {({ item, rowId }) => (
                    <DataGridRow<ProtocolConsumption>
                      key={rowId}
                      // El click abre el detalle. Va en la fila y no en cada celda:
                      // un unico handler, y Enter sobre la celda enfocada burbujea hasta aca.
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        openConsumption(item);
                      }}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter') {
                          e.stopPropagation();
                          openConsumption(item);
                        }
                      }}
                    >
                      {(column) => (
                        <DataGridCell className={`bgrid-cell bgrid-cell--${column.columnId}`}>
                          {column.renderCell(item)}
                        </DataGridCell>
                      )}
                    </DataGridRow>
                  )}
                </DataGridBody>
              </DataGrid>
            </div>

            {/* Los dos grupos se totalizan por separado: lo que suma al costo de
                la visita y lo que no. */}
            <div className="billedGrid-totals">
              <div className="billedGrid-total billedGrid-total--cost">
                <span className="billedGrid-total-label">
                  Costo de la visita
                  <span className="billedGrid-total-count">
                    {totals.cost.count} {totals.cost.count === 1 ? 'concepto' : 'conceptos'}
                  </span>
                </span>
                <span className="billedGrid-total-value">{fmtTotals(totals.cost)}</span>
              </div>

              <div className="billedGrid-total">
                <span className="billedGrid-total-label">
                  Fuera del costo
                  <span className="billedGrid-total-count">
                    {totals.nonCost.count} {totals.nonCost.count === 1 ? 'concepto' : 'conceptos'}
                  </span>
                </span>
                <span className="billedGrid-total-value">{fmtTotals(totals.nonCost)}</span>
              </div>
            </div>
          </>
        )}
      </div>
      {Forms}
    </>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(BilledInConceptListComponent as any);
